/**
 * Authentication Initialization Hook
 * Handles authentication initialization on app startup and page refresh
 */

import { message } from "antd";
import type { User as OidcUser } from "oidc-client-ts";
import { useCallback } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import type { AuthParamsSnapshot } from "@/contexts/auth/authParams";
import { SSO_AUTH_PROGRESS_KEY } from "@/contexts/auth/constants";
import { generateCodeVerifier } from "@/lib/auth/pkce";
import * as storage from "@/lib/storage";
import { Logger } from "@/utils";

interface UseAuthInitializationProps {
  processAuthParams: (params?: AuthParamsSnapshot) => Promise<boolean>;
  handleOidcLogin: (
    accessToken?: string,
    refreshToken?: string,
  ) => Promise<{
    userData: unknown;
    shouldRedirect: boolean;
  }>;
  restoreUserSession: () => Promise<boolean>;
  logout: () => void;
  getRedirectPath: () => string;
  safeRedirect: (path: string) => void;
  setLoading: (loading: boolean) => void;
}

interface UseAuthInitializationReturn {
  initializeAuth: () => Promise<void>;
  handleOidcUserLoaded: (user: OidcUser) => void;
}

/**
 * Custom hook for authentication initialization
 */
export const useAuthInitialization = ({
  processAuthParams,
  handleOidcLogin,
  restoreUserSession,
  logout,
  getRedirectPath,
  safeRedirect,
  setLoading,
}: UseAuthInitializationProps): UseAuthInitializationReturn => {
  const oidcAuth = useOidcAuth();

  /**
   * Initialize authentication on app startup
   */
  const initializeAuth = useCallback(async () => {
    try {
      // Ensure code verifier exists
      if (!storage.sessionStore.safeGet(storage.CODE_VERIFIER_KEY)) {
        const newVerifier = generateCodeVerifier();
        storage.sessionStore.safeSet(storage.CODE_VERIFIER_KEY, newVerifier);
      }

      const isLoginRoute = window.location.pathname === "/login";
      const authParamsProcessed = isLoginRoute ? false : await processAuthParams();

      // 1. OIDC authentication success
      if (!authParamsProcessed && oidcAuth.isAuthenticated && oidcAuth.user) {
        const result = await handleOidcLogin(
          oidcAuth.user.access_token,
          oidcAuth.user.refresh_token,
        );

        if (result.shouldRedirect) {
          const targetPath = getRedirectPath();
          sessionStorage.removeItem(SSO_AUTH_PROGRESS_KEY);
          safeRedirect(targetPath);
        }
      }
      // 2. Restore user from local storage (when refresh page)
      else if (!authParamsProcessed && !oidcAuth.isAuthenticated) {
        const restored = await restoreUserSession();

        if (restored) {
          const targetPath = getRedirectPath();
          safeRedirect(targetPath);
        } else {
          const hasStoredUser = sessionStorage.getItem("user");
          if (hasStoredUser) {
            // Token is invalid, logout
            Logger.error("Invalid token detected, logging out");
            message.error("Session expired. Please login again.");
            logout();
          }
        }
      }
    } catch (error) {
      Logger.error("Auth initialization error:", error);
      message.error(
        `Authentication error: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setLoading(false);
    }
  }, [
    processAuthParams,
    oidcAuth.isAuthenticated,
    oidcAuth.user,
    handleOidcLogin,
    restoreUserSession,
    logout,
    getRedirectPath,
    safeRedirect,
    setLoading,
  ]);

  /**
   * Handle OIDC user loaded event
   */
  const handleOidcUserLoaded = useCallback((_user: OidcUser) => {
    if (sessionStorage.getItem(SSO_AUTH_PROGRESS_KEY) === "true") {
      Logger.info("OIDC user loaded; waiting for token persistence before redirect");
    }
  }, []);

  return {
    initializeAuth,
    handleOidcUserLoaded,
  };
};
